import { Card as CardId, Seat, cardRank, cardSuit } from "@ravalmatic/bridge-engine/core";
import { HandMode } from "../../Types";
import { GameVars } from "../../GameVars";
import { Card, CardPresentation } from "./Card";
import { isLocalPlayerSeat } from "./PlayerSeats";

export class PlayerHand extends Phaser.GameObjects.Container {

    private static readonly CARD_SPACING = 40;
    private static readonly SUIT_SPACING = 4;
    private static readonly REVEAL_DELAY = 60;
    private static readonly FLIP_DELAY = 51;
    private static readonly FLIP_START_DELAY = 250;
    private static readonly MOVE_DURATION = 300;
    private static readonly SUIT_GAP_DURATION = 600;

    public readonly seat: Seat;
    public cards: readonly CardId[];
    public mode: HandMode;

    private cardViews: Card[];
    private onCardSelected?: (seat: Seat, card: CardId) => void;
    private playFeedbackEnabled = false;
    private playEnabled = false;
    private revealGeneration = 0;

    constructor(scene: Phaser.Scene, seat: Seat, cards: readonly CardId[], mode: HandMode = HandMode.HIDDEN) {

        super(scene);

        this.seat = seat;
        this.cards = [...cards];
        this.mode = mode;
        this.createCards();
    }

    public setCardSelectedHandler(handler: (seat: Seat, card: CardId) => void): void {

        this.onCardSelected = handler;
    }

    public setPlayFeedbackEnabled(enabled: boolean): void {

        this.playFeedbackEnabled = enabled;
        this.cardViews.forEach(card => card.setPlayFeedbackEnabled(
            this.isPlayFeedbackActive(), GameVars.gameData.playableCardHints
        ));
    }

    public getCardPresentation(
        card: CardId, relativeTo?: Phaser.GameObjects.Container
    ): CardPresentation | undefined {

        return this.cardViews.find(view => view.cardId === card)?.getPresentation(relativeTo);
    }

    public startBiddingEntrance(): void {

        const generation = this.revealGeneration;
        const reveal = () => {
            if (!this.scene || this.revealGeneration !== generation || this.mode !== HandMode.HIDDEN) {
                return;
            }
            this.updatePlayHand(this.cards, HandMode.INTERACTIVE, []);
            this.setPlayEnabled(false);
        };
        if (this.cardViews.length === 0) {
            reveal();
            return;
        }
        let cardsArrived = 0;
        this.cardViews.forEach(card => {
            card.y = Card.HEIGHT;
            this.scene.tweens.add({
                targets: card,
                y: 0,
                duration: PlayerHand.MOVE_DURATION,
                ease: "Cubic.Out",
                onComplete: () => {
                    cardsArrived++;
                    if (cardsArrived === this.cardViews.length) {
                        reveal();
                    }
                }
            });
        });
    }

    public updatePlayHand(cards: readonly CardId[], mode: HandMode, legalCards: readonly CardId[]): void {

        const visibilityChanged = (mode === HandMode.HIDDEN) !== (this.mode === HandMode.HIDDEN);
        const cardsChanged = cards.length !== this.cards.length
            || cards.some((card, index) => card !== this.cards[index]);
        if (visibilityChanged || cardsChanged) {
            const revealGeneration = ++this.revealGeneration;
            const reveal = this.mode === HandMode.HIDDEN && mode !== HandMode.HIDDEN;
            const previousViews = new Map(this.cardViews.map(view => [view.cardId, view]));
            const cardsRemoved = this.cardViews.some(view => !cards.includes(view.cardId));
            const keepHiddenPositions = this.seat === 0 && this.mode === HandMode.HIDDEN
                && mode === HandMode.HIDDEN && cards.every(card => previousViews.has(card));
            const keepLateralPositions = (this.seat === 1 || this.seat === 3)
                && cards.every(card => previousViews.has(card));
            const revealLocalHand = reveal && isLocalPlayerSeat(this.seat) && !cardsRemoved
                && this.cardViews.length > 0;
            this.cardViews.filter(view => !cards.includes(view.cardId)).forEach(view => view.destroy());
            this.cards = [...cards];
            this.mode = mode;
            this.cardViews = this.orderedCards().map(card => previousViews.get(card) ?? this.createCard(card));
            this.removeAll();
            this.add(this.cardViews);
            if (cardsRemoved) {
                this.layoutCards(true, false);
            } else if (keepLateralPositions) {
                this.cardViews.forEach(view => {
                    view.resetRaisedPosition();
                    this.scene.tweens.killTweensOf(view);
                });
            } else if (!keepHiddenPositions && !revealLocalHand) {
                const revealNorthHand = reveal && this.seat === 0;
                this.layoutCards(
                    reveal || (cardsChanged && (this.seat !== 0 || mode !== HandMode.HIDDEN)),
                    !revealNorthHand,
                    revealNorthHand ? PlayerHand.SUIT_GAP_DURATION : PlayerHand.MOVE_DURATION
                );
            }
            if (visibilityChanged) {
                this.cardViews.forEach((view, index) => {
                    if (mode === HandMode.HIDDEN) view.unFlip();
                    else view.flip(
                        reveal,
                        reveal ? PlayerHand.FLIP_START_DELAY + index * PlayerHand.FLIP_DELAY : 0,
                        revealLocalHand && index === this.cardViews.length - 1
                            ? () => {
                                if (this.scene && this.revealGeneration === revealGeneration) {
                                    this.layoutCards(true, false, PlayerHand.SUIT_GAP_DURATION);
                                }
                            } : undefined
                    );
                });
            }
        }
        this.mode = mode;
        this.setPlayEnabled(mode === HandMode.INTERACTIVE, legalCards);
    }

    private orderedCards(): CardId[] {

        return [...this.cards].sort((first, second) =>
            cardSuit(first) - cardSuit(second) || cardRank(second) - cardRank(first)
        );
    }

    private createCard(card: CardId): Card {

        const view = new Card(
            this.scene, card, this.mode !== HandMode.HIDDEN, false,
            selected => this.onCardSelected?.(this.seat, selected),
            this.seat === 0 ? 12 : this.seat === 2 ? -12 : 0
        );
        view.setPlayFeedbackEnabled(this.isPlayFeedbackActive(), GameVars.gameData.playableCardHints);
        return view;
    }

    private createCards(): void {

        this.cardViews = this.orderedCards().map(card => this.createCard(card));
        this.add(this.cardViews);
        this.layoutCards(false);
    }

    private layoutCards(animated: boolean, staggered = true, duration = PlayerHand.MOVE_DURATION): void {

        const positions: number[] = [];
        let cardPosition = 0;
        this.cardViews.forEach((view, index) => {
            positions.push(cardPosition);
            const nextCard = this.cardViews[index + 1];
            if (nextCard !== undefined) {
                cardPosition += this.seat !== 1 && this.seat !== 3
                    && this.mode !== HandMode.HIDDEN && cardSuit(view.cardId) !== cardSuit(nextCard.cardId)
                    ? Card.WIDTH + PlayerHand.SUIT_SPACING
                    : PlayerHand.CARD_SPACING;
            }
        });

        const handOffset = cardPosition / 2;
        this.cardViews.forEach((view, index) => {
            const x = positions[index] - handOffset;
            view.resetRaisedPosition();
            this.scene.tweens.killTweensOf(view);
            if (animated && view.x !== x) {
                this.scene.tweens.add({
                    targets: view,
                    x,
                    duration,
                    delay: staggered ? index * PlayerHand.REVEAL_DELAY : 0,
                    ease: "Cubic.Out",
                });
            } else {
                view.x = x;
            }
        });
    }

    public setPlayEnabled(enabled: boolean, legalCards: readonly CardId[] = this.cards): void {

        this.playEnabled = enabled;
        const feedbackEnabled = this.isPlayFeedbackActive();
        this.cardViews.forEach(card => {
            card.setPlayable(
                enabled && this.mode === HandMode.INTERACTIVE && legalCards.includes(card.cardId),
                feedbackEnabled,
                GameVars.gameData.playableCardHints
            );
        });
    }

    public isPlayEnabled(): boolean {

        return this.playEnabled;
    }

    public stopAnimations(): void {

        this.revealGeneration++;
        this.scene.tweens.killTweensOf(this);
        this.cardViews.forEach(card => card.stopAnimations());
    }

    private isPlayFeedbackActive(): boolean {

        return this.playFeedbackEnabled && this.playEnabled && this.mode === HandMode.INTERACTIVE
            && (this.seat === 0 || this.seat === 2);
    }
}
